<template>
    <transition name="fade">
        <div :style="{ height: getContainerHeight }" class="chatbot-fixed">
            <div class="chatbot-fixed__chat-container" :style="{ height: getContainerHeight }" v-if="isChatDataLoaded">
                <ChatBotHeader :isChatDataLoaded="isChatDataLoaded" @confirm-new-chat-session="confirmNewChatSession"
                @close-chatbot="closeChatbot"/>
                <FeedbackPanel :feedbackType="feedbackType" :feedbackMessageId="feedbackId"
                    @update-feedback-icons="updateFeedbackIcons" />
                <ResetChatSessionPrompt ref="resetChat" @open-new-chat-session="resetChatSession"
                    @cancel-chat-session="cancelChatSession" />
                <deep-chat :submitButtonStyles.prop="getSubmitButtonStyles"
                    :inputAreaStyle.prop="getInputAreaStyles" :messageStyles.prop="getMessageStyles"
                    :names.prop="getNameSettings" :initialMessages.prop="chatHistory"
                    :introMessage.prop="getIntroMessage" :style="getGeneralStyles"
                    :textInput.prop="getTextInputSettings" ref="chatbot" :request.prop="requestHandler"
                    :htmlClassUtilities.prop="getHtmlClassUtilities"
                    :avatars.prop="getAvatars"
                >
                </deep-chat>
                <div class="chatbot-fixed__footer">
                    <span class="chatbot-fixed__footer-text" v-html="getFooterText"></span>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    import 'deep-chat'
    import { mapGetters } from 'vuex'
    import ChatBotStyles from 'components/chatbot/assistant/helpers/ChatBotStyles'
    import FeedbackPanel from 'components/chatbot/assistant/feedback/FeedbackPanel'
    import FeedbackHelper from 'components/chatbot/assistant/helpers/FeedbackHelper'
    import ResetChatSessionPrompt from 'components/chatbot/assistant/prompts/ResetChatSessionPrompt'
    import ChatBotHeader from 'components/chatbot/assistant/header/ChatBotHeader'
    import FormattingHelper from 'components/chatbot/assistant/helpers/FormattingHelper'

    export default {
        name: 'EvaChatBot',
        mixins: [ChatBotStyles, FeedbackHelper, FormattingHelper],
        components: {
            FeedbackPanel,
            ResetChatSessionPrompt,
            ChatBotHeader
        },
        data () {
            return {
                requestHandler: {
                    handler: async (body, signals) => {
                        try {
                            this.setChatStateAtRequestTime()
                            await this.$store.dispatch('ChatBot/sendMessage', { message: body.messages[0].text, language: this.language }).then((response) => {
                                signals.onOpen()
                                signals.onResponse({
                                    html: `<div class="message" id="message_${response.id}"></div>
                                ${this.getRecommendedMessageLink(response)}
                                 <div class="feedback-container" id="feedback-container_${response.id}">
                                    <span class="feedback-text">${this.getFeedBackText}</span>
                                    <img class="feedback-icon positive" id="positive_${response.id}" src=${this.thumbsUpImage} />
                                    <img class="feedback-icon negative" id="negative_${response.id}" src=${this.thumbsDownImage} />
                                 </div>
                                ` })
                                this.setResponse(response)
                                signals.onClose()
                            })

                            if (!this.chatStateModified) {
                                await this.streamSimulation(this.response)
                            }
                        } catch (error) {
                            if (error instanceof Error) {
                                switch (error.response.data.code) {
                                case 'completion_failed':
                                    signals.onResponse({ html: this.getErrorMessage('Chatbot_error_service_not_available'), 'role': 'error' })
                                    break
                                case 'prompt_filtered':
                                    signals.onResponse({ html: this.getErrorMessage('Chatbot_error_request_cannot_be_processed'), 'role': 'error' })
                                    break
                                case 'rate_limit_exceeded':
                                    signals.onResponse({ html: this.getErrorMessage('Chatbot_error_rate_limit_exceeded'), 'role': 'error' })
                                    break
                                case 'service_unavailable':
                                    signals.onResponse({ html: this.getErrorMessage('Chatbot_error_service_not_available'), 'role': 'error' })
                                    break
                                default:
                                    signals.onResponse({ html: this.getErrorMessage('Chatbot_error_unexpected_error_occured'), 'role': 'error' })
                                }
                            } else {
                                signals.onResponse({ html: `<div class="error-message">${this.getErrorMessage('Chatbot_error_unexpected_error_occured')}</div>`, 'role': 'error' })
                            }
                        }
                    }
                },
                isChatDataLoaded: false,
                thumbsUpImage: require('@/assets/images/chatbot/thumbsup.svg'),
                thumbsDownImage: require('@/assets/images/chatbot/thumbsdown.svg'),
                linkImage: require('@/assets/images/chatbot/link_icon.png'),
                suggestionIcon: require('@/assets/images/chatbot/suggestion_icon.svg'),
                evaAvatar: require('@/assets/images/chatbot/eva_simple.png'),
                feedbackType: '',
                feedbackId: null,
                currentFeedbackElement: null,
                response: null,
                tickAmount: 0,
                chatStateModified: false
            }
        },
        computed: {
            ...mapGetters({
                currentChat: 'ChatBot/getCurrentChat',
                chatSessions: 'ChatBot/getChatSessions',
                showChatbot: 'ChatBot/getShowChatBot',
                chatHistory: 'ChatBot/getChatHistory',
                language: 'Languages/getSelectedCountryCode',
                currentChatMode: 'ChatBotCombo/getCurrentChatMode',
                currentUser: 'User/getCurrentUser'
            }),
            getIntroMessage () {
                return {
                    html: this.$t('Chatbot.intro_message', { userName: this.currentUser.firstName }),
                    role: 'ai'
                }
            },
            isThereChatHistory () {
                if (this.chatHistory === null) {
                    return false
                }
                return this.chatHistory.length > 0
            },
            isThereChatSessions () {
                return this.chatSessions && this.chatSessions.length > 0
            },
            getFooterText () {
                return this.$t('Chatbot.beta_notification')
            },
            getContainerHeight () {
                if (this.isMobileSmallView) {
                    return window.innerHeight + 'px'
                }
                return ''
            }
        },
        methods: {
            getErrorMessage (domain) {
                return `<div class="error-message">${this.$t(domain)}</div>`
            },
            async closeChatbot () {
                document.body.classList.remove('eva-chatbot-open-mobile')
                await this.$store.dispatch('ChatBot/setShowChatBot', false)
                this.chatStateModified = true
            },
            async openNewChatSession () {
                await this.$store.dispatch('ChatBot/openNewChatSession')
                this.createSuggestionButtons()
                this.chatStateModified = true
            },
            async resetChatSession () {
                this.openNewChatSession()
                this.$refs.resetChat.$el.classList.toggle('slide-down')
            },
            scrollChatToBottom () {
                if (this.$refs.chatbot) {
                    this.$refs.chatbot.scrollToBottom()
                }
            },
            cancelChatSession () {
                this.$refs.resetChat.$el.classList.toggle('slide-down')
                this.chatStateModified = true
            },
            confirmNewChatSession () {
                this.$refs.resetChat.$el.classList.toggle('slide-down')
                this.chatStateModified = true
            },
            setResponse (response) {
                this.response = response
            },
            setChatStateAtRequestTime () {
                this.chatStateModified = false
            },
            async handleLanguageChange () {
                this.chatStateModified = true
                if (this.isThereChatSessions && this.$refs.chatbot) {
                    await this.$store.dispatch('ChatBot/loadSingleChatHistory', this.chatSessions[0].id)
                    if (this.chatHistory.length === 0) {
                        this.waitForChatbotAndCreateSuggestions()
                    }
                }
            },
            waitForChatbotAndCreateSuggestions () {
                if (this.$refs.chatbot) {
                    this.createSuggestionButtons()
                    this.tickAmount = 0
                } else {
                    if (this.tickAmount < 10) {
                        this.tickAmount++
                        this.$nextTick(this.waitForChatbotAndCreateSuggestions)
                    } else {
                        this.tickAmount = 0
                    }
                }
            },
        },
        async created () {
            await this.$store.dispatch(('ChatBot/refreshChatToken'))
            await this.$store.dispatch('ChatBot/loadChatSessions')
            if (this.isThereChatSessions) {
                await this.$store.dispatch('ChatBot/loadSingleChatHistory', this.chatSessions[0].id)
            } else {
                this.openNewChatSession()
            }
            this.isChatDataLoaded = true
        },
        watch: {
            language (newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.handleLanguageChange()
                }
            },
            chatHistory (newValue, oldValue) {
                if (newValue && newValue.length === 0) {
                    this.waitForChatbotAndCreateSuggestions()
                }
            },
            currentChatMode (newValue) {
                if (newValue === 'eva') {
                    this.scrollChatToBottom()
                }
            },
            immediate: true
        }
    }
</script>

<style lang="scss">
    textarea {
        resize: none;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.4s ease, transform 0.4s ease;
        transform: translateX(0);
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
        transform: translateX(10px)
    }

    .chatbot-fixed {
        flex-direction: column;
        font-family: Nunito-Regular, sans-serif;
        display: flex;
        bottom: 10px;
        z-index: 9991;
        width: 500px;
        height: 100%;

        [class="mobile_sm"] & {
            width: 100%;
            height: 100%;
        }

        &__chat-container {
            width: 100%;
            box-shadow: 2px 2px 8px 0px #00000040;
            background: $alabaster;
            border-radius: 0px 0px 10px 10px;
            width: 500px;
            display: flex;
            flex-direction: column;
            height: 100%;

            [class="mobile_sm"] & {
                height: 100%;
                width: 100%;
                border-radius: 0;
            }
        }

        &__footer {
            text-align: center;
            padding: 0 30px 15px 30px;

            a {
                text-decoration: underline;
            }
        }

        &__footer-text {
            width: 100%;
            justify-content: center;
            font-size: 1.2rem;
            color: $dove_gray;

            a {
                color: $dove_gray !important;
            }

            [class="mobile_sm"] & {
                font-size: 1rem;
            }
        }
    }
</style>
